import { TextField } from '@/components/ui';
import { formatINR } from '@/utils/format';
import { CASH_DENOMINATIONS, notesTotal, parseNotes, type NotesDraft } from '../cashNotes';
import styles from '@/styles/components/CashNotesField/style.scss';

interface CashNotesFieldProps {
  value: NotesDraft;
  onChange: (value: NotesDraft) => void;
  disabled?: boolean;
  /** Under the total (e.g. how a past day's count is recorded). */
  hint?: string;
}

/**
 * The cash count as notes: one row per note (₹500 × count = amount), then the coins as one ₹ amount (₹5, ₹2 and ₹1
 * aren't counted by value), then the total, which is the count. A field left empty counts as 0.
 */
export function CashNotesField({ value, onChange, disabled, hint }: CashNotesFieldProps) {
  const notes = parseNotes(value);

  return (
    <fieldset className={styles.field} disabled={disabled}>
      <legend className={styles.legend}>Cash in the counter</legend>
      <ul className={styles.list}>
        {CASH_DENOMINATIONS.map((d, i) => {
          const count = value[d];
          const parsed = count.trim() === '' ? 0 : Number(count);
          return (
            <li key={d} className={styles.row}>
              <span className={styles.note}>₹{d}</span>
              <span className={styles.times} aria-hidden>
                ×
              </span>
              <TextField
                aria-label={`Number of ₹${d} notes`}
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                placeholder="0"
                mono
                size="md"
                value={count}
                onChange={(e) => onChange({ ...value, [d]: e.target.value })}
                data-autofocus={i === 0 ? true : undefined}
              />
              <span className={styles.amount}>{Number.isInteger(parsed) && parsed > 0 ? formatINR(d * parsed) : '—'}</span>
            </li>
          );
        })}
        <li className={styles.row}>
          <span className={styles.coins}>Coins</span>
          <TextField
            aria-label="Coins, in rupees"
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            placeholder="0"
            mono
            size="md"
            value={value.coins}
            onChange={(e) => onChange({ ...value, coins: e.target.value })}
          />
          <span className={styles.amount}>{notes && notes.coins > 0 ? formatINR(notes.coins) : '—'}</span>
        </li>
      </ul>
      <div className={styles.total}>
        <span className={styles.totalLabel}>Total</span>
        <span className={styles.totalValue}>{notes ? formatINR(notesTotal(notes)) : '—'}</span>
      </div>
      {hint && <span className={styles.hint}>{hint}</span>}
    </fieldset>
  );
}
